/* eslint-disable @typescript-eslint/no-explicit-any */
import { prePageCall } from '@marriott/mi-headless-utils';
import { GetServerSideProps } from 'next';
//This returns a component which can be rendered in any product specific [[page.tsx]]
//to return the page template(Head, main, sections) along with the required react Components mentioned in model.json and
// load the CSS and JS libs required by static components from AEM.
import { ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { Layout, respGridUtil } from '@marriott/mi-headless-utils';
import { Types } from '@marriott/mi-ui-library';
import clsx from 'clsx';
import { getDeployedEnvType, getNextMiApolloClient } from '@marriott/mi-apollo-client-utils';
import { phoenixCobrandECMQuery } from '@marriott/mi-cobrand-graphql';
import { PageContext } from '@marriott/mi-cobrand-components';
import { logger } from '@marriott/mi-headless-utils';
import { fetchFlag, FEATURE_FLAGS, TARGETS } from '@marriott/mi-feature-flag-utils';
import { resolveTargetCountryCode } from '../_constants/creditCardsVisibilityConfig';
import { envVarKeys } from '../_constants/envVarKeys';
import { ECM_ENDPOINT } from '../_constants/constants';
import operationSignatures from '../_constants/operationSignatures.json';
import {
  buildRedirectResponse,
  checkEcmEligibility,
  getAkamaiCountryCode,
  getCreditCardsLocaleTarget,
  getSessionAuthState,
  isMetaInAppUserAgent,
  removeConfiguredCreditCardsComponentsFromModel,
  shouldHideCreditCardsComponents,
  transformCobrandInterstitialForMetaInApp,
  validateAndParseCreditCardsVisibilityConfig,
} from '../helper';
const { log } = logger({})('credit-cards.tsx');

interface CreditCardsLandingPageProps {
  model: any;
  pagePath: string;
  isAuthorMode: boolean;
  currentLocale?: string;
  sessionData?: any;
}

export default function CreditCardsLandingPage({
  model,
  pagePath,
  isAuthorMode,
  currentLocale,
  sessionData,
}: CreditCardsLandingPageProps) {
  const {
    metaNames,
    metaProperties,
    title,
    staticComponentClientLibsCSS,
    staticComponentClientLibsJS,
    gridCss,
    canonical,
    hreflangList,
  } = model;

  const respGridModel = respGridUtil(model?.cqItems?.root?.[':items']?.responsivegrid, { normalizeTracking: true });

  return (
    <PageContext.Provider value={{ eliteLevelCode: model?.eliteLevelCode ?? null, sessionData, currentLocale }}>
      <Layout
        metaNames={metaNames}
        metaProps={metaProperties}
        title={title}
        staticComponentClientLibsCSS={staticComponentClientLibsCSS}
        staticComponentClientLibsJS={staticComponentClientLibsJS}
        isAuthorMode={isAuthorMode}
        gridCss={gridCss}
        canonical={canonical}
        hreflangList={hreflangList}
      >
        <section>
          <div className={clsx(Types.Themes.Alternate)}>
            <ResponsiveGrid
              key={pagePath}
              {...respGridModel}
              model={respGridModel}
              pagePath={pagePath}
              itemPath="root/responsivegrid"
            />
          </div>
        </section>
      </Layout>
    </PageContext.Provider>
  );
}

const cobrandSpecificData = async (context: any, parallelCallsArr: any) => {
  const query = new URLSearchParams(context.query);
  let showError = false;
  const sessionState = getSessionAuthState(parallelCallsArr);
  const featureFlagLogo = await fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_LOGO_AUTOMATION, {
    target: TARGETS.APP.COBRAND_RENDERER,
  });
  const ecmResult = await checkEcmEligibility(context, parallelCallsArr, {
    operationSignatures,
    apolloClientUtils: {
      getNextMiApolloClient,
      getDeployedEnvType,
    },
    phoenixCobrandECMQuery,
  });

  // Enhanced logging for debugging
  log.debug(
    `[rendererlog] ECM Result: ${JSON.stringify({
      hasEligibleCard: ecmResult.hasEligibleCard,
      selectedCardCode: ecmResult.selectedCardCode,
      selectedCardParam: ecmResult.selectedCardParam,
      userCards: ecmResult.userCards,
      sessionState,
      profileCountryCode: ecmResult.profileCountryCode,
      eliteLevelCode: ecmResult.eliteLevelCode,
      hasError: !!ecmResult.error,
    })}`
  );

  if (ecmResult.error) {
    log.error(`[rendererlog] GraphQL API call failed, error: ${JSON.stringify(ecmResult.error)}`);
  }

  if (query.get('error') && query.get('error') === 'true') {
    showError = true;
  }

  if (ecmResult.productSpecificSeoUrl) {
    log.debug(`[rendererlog] Fetching AEM content from: ${ecmResult.productSpecificSeoUrl}`);
  } else {
    log.debug(
      `[rendererlog] No eligible card detected - hasEligibleCard: ${ecmResult.hasEligibleCard}, selectedCardParam: ${ecmResult.selectedCardParam}`
    );
  }

  return {
    operationSignatures: [],
    NEXT_PUBLIC_FETCH_OFFERS_API_URL: process.env.NEXT_PUBLIC_FETCH_OFFERS_API_URL,
    nextPublicEnvKeys: [...Object.keys(envVarKeys)],
    showError,
    hasEligibleCard: ecmResult.hasEligibleCard,
    selectedCardParam: ecmResult.selectedCardParam,
    productSpecificSeoUrl: ecmResult.productSpecificSeoUrl, // This tells prePageCall which AEM page to fetch
    profileCountryCode: ecmResult.profileCountryCode,
    sessionState,
    token: parallelCallsArr?.sessionData?.cacheData?.data?.accessToken,
    ecmError: ecmResult.error, // Pass error through for debugging
    featureFlagLogo: featureFlagLogo ?? null,
    eliteLevelCode: ecmResult.eliteLevelCode ?? null,
  };
};

const mockModel = async () => {
  if (process.env['NODE_ENV'] === 'development') {
    // nx-ignore-next-line
    const { creditCardsPageModel } = await import('../_localDevMocks/credit-cards-page.model'); //Load mock model dynamically only for dev mode
    return creditCardsPageModel;
  }
  return {};
};

const postAEMModelOperations = async (...args: any[]) => {
  const [context, parallelCallsArr, interceptorResponse, model, productSpecificProps] = args;
  const eliteLevelCode = productSpecificProps?.eliteLevelCode ?? null;
  const userAgentHeader = context?.req?.headers?.['user-agent'] || '';
  const isMetaInAppUA = isMetaInAppUserAgent(userAgentHeader);
  let updatedModel = transformCobrandInterstitialForMetaInApp(model, isMetaInAppUA);

  try {
    const creditCardsVisibilityFlag = await fetchFlag(
      FEATURE_FLAGS.EXPERIENCE_MCOM_COBRAND_COMPONENT_VISIBILITY_CONFIG,
      {
        target: TARGETS.APP.COBRAND_RENDERER,
      }
    );
    log.debug(
      `[rendererlog] Credit cards visibility feature flag response: ${JSON.stringify(creditCardsVisibilityFlag)}`
    );
    const visibilityConfig = validateAndParseCreditCardsVisibilityConfig(creditCardsVisibilityFlag);
    const resolvedUrl = context?.resolvedUrl || context?.req?.url || null;

    if (visibilityConfig) {
      const matchedLocaleTarget = getCreditCardsLocaleTarget(
        context,
        interceptorResponse,
        updatedModel,
        productSpecificProps?.currentLocale,
        visibilityConfig.localeConfig
      );
      const targetCountryCode = resolveTargetCountryCode(matchedLocaleTarget);
      const browserCountryCode = getAkamaiCountryCode(context?.req?.headers?.['x-akamai-edgescape']);
      const sessionState = productSpecificProps?.sessionState || getSessionAuthState(parallelCallsArr);
      const componentRemovalRules =
        (matchedLocaleTarget && visibilityConfig.componentRemovalConfig?.[matchedLocaleTarget]?.rules) || [];
      const shouldHideComponents =
        Boolean(matchedLocaleTarget) &&
        shouldHideCreditCardsComponents({
          sessionState,
          browserCountryCode,
          profileCountryCode: productSpecificProps?.profileCountryCode,
          targetCountryCode,
        });

      log.debug(
        `[rendererlog] Credit cards visibility inputs: ${JSON.stringify({
          resolvedUrl,
          currentLocale: productSpecificProps?.currentLocale || context?.locale || null,
          matchedLocaleTarget,
          targetCountryCode,
          browserCountryCode,
          sessionState,
          profileCountryCode: productSpecificProps?.profileCountryCode || null,
          configuredRuleCount: componentRemovalRules.length,
        })}`
      );

      if (matchedLocaleTarget && shouldHideComponents) {
        updatedModel = removeConfiguredCreditCardsComponentsFromModel(updatedModel, componentRemovalRules);
      }

      log.debug(
        `[rendererlog] Credit cards visibility decision: ${JSON.stringify({
          matchedLocaleTarget,
          targetCountryCode,
          browserCountryCode,
          sessionState,
          profileCountryCode: productSpecificProps?.profileCountryCode || null,
          shouldHideComponents,
        })}`
      );
    } else {
      log.debug(
        `[rendererlog] Credit cards visibility config unavailable or invalid; leaving model unchanged for ${resolvedUrl}`
      );
    }
  } catch (error) {
    log.warn(`[rendererlog] Credit cards visibility flag evaluation failed: ${JSON.stringify(error)}`);
  }

  const enableWebSDK = String(updatedModel?.enableWebSDK);

  const ENABLE_COBRAND_ECM = await fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_COBRAND_ENABLE_ECM, {
    target: TARGETS.APP.COBRAND_RENDERER,
  });

  if (enableWebSDK === 'true' && process.env['NEXT_PUBLIC_ENABLE_WEBSDK'] === 'true') {
    const ENABLE_CLIENT_DATA_LAYER = await fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_RTP_ENABLE_ACDL, {
      target: TARGETS.APP.COBRAND_RENDERER,
    });
    return {
      ...updatedModel,
      ENABLE_ACDL: ENABLE_CLIENT_DATA_LAYER?.experience_mcom_rtp_enable_acdl,
      ENABLE_ECM: ENABLE_COBRAND_ECM?.experience_mcom_cobrand_enable_ecm,
      eliteLevelCode,
    };
  } else {
    return {
      ...updatedModel,
      ENABLE_ACDL: false,
      ENABLE_ECM: false,
      eliteLevelCode,
    };
  }
};

const wrappedPrePageCall = prePageCall(
  cobrandSpecificData,
  mockModel,
  false,
  false,
  false,
  undefined,
  postAEMModelOperations
);

//SSR Render

export const getServerSideProps: GetServerSideProps<any> = async context => {
  const result = await wrappedPrePageCall(context);

  // Enhanced logging
  log.debug(
    `[rendererlog] getServerSideProps result: ${JSON.stringify({
      hasToken: !!result?.props?.token,
      hasEligibleCard: result?.props?.hasEligibleCard,
      selectedCardParam: result?.props?.selectedCardParam,
      productSpecificSeoUrl: result?.props?.productSpecificSeoUrl,
      hasEcmError: !!result?.props?.ecmError,
    })}`
  );

  // Redirect user to the ECM page if logged in with an eligible card
  if (result?.props?.token && result?.props?.hasEligibleCard === true) {
    const selectedCardParam = result?.props?.selectedCardParam;
    log.debug(`[rendererlog] Redirecting: User is authenticated and holds an eligible card (${selectedCardParam})`);

    const query = result?.props?.query;
    return buildRedirectResponse(ECM_ENDPOINT, query);
  }

  // Log if GraphQL API failed but user might have a card
  if (result?.props?.ecmError) {
    log.warn(
      `[rendererlog] GraphQL API error detected, user may have eligible card but cannot be verified: ${JSON.stringify(
        result.props.ecmError
      )}`
    );
  }

  return result;
};
